let installed = false;
let onDomainError = null;
let fetchProbe = null;

var domainProbeCache = new Map();
var DOMAIN_PROBE_TTL = 60 * 1000;
var PROBE_TIMEOUT_MS = 5000;

const resolveUrl = (url, baseUrl) => {
	if (!url || typeof URL === 'undefined') {
		return null;
	}

	try {
		return new URL(url, baseUrl || window.location.origin);
	} catch (e) {
		return null;
	}
};

const isExternalHttps = (parsedUrl) => {
	if (!parsedUrl || parsedUrl.protocol !== 'https:') {
		return false;
	}

	if (typeof window === 'undefined') {
		return true;
	}

	return parsedUrl.hostname !== window.location.hostname;
};

const isOffline = () => {
	if (typeof navigator === 'undefined') {
		return false;
	}

	return navigator.onLine === false;
};

const isHttpError = (error) => {
	if (!error) {
		return false;
	}

	if (typeof error.status === 'number' && error.status > 0) {
		return true;
	}

	if (error.response && typeof error.response.status === 'number') {
		return true;
	}

	if (typeof Response !== 'undefined' && error instanceof Response) {
		return true;
	}

	return false;
};

const shouldSkipError = (error) => {
	return isCanceledError(error) || isHttpError(error);
};

const getProbeCache = (domain) => {
	return domain ? domainProbeCache.get(domain) : null;
};

const setProbeCache = (domain, entry) => {
	if (!domain) {
		return;
	}

	domainProbeCache.set(domain, entry);
};

const probeDomain = (parsedUrl) => {
	if (!fetchProbe || !parsedUrl) {
		return Promise.resolve(true);
	}

	var domain = parsedUrl.host;
	var now = Date.now();
	var cached = getProbeCache(domain);

	if (cached && cached.promise) {
		return cached.promise;
	}

	if (cached && typeof cached.checkedAt === 'number' && (now - cached.checkedAt) < DOMAIN_PROBE_TTL) {
		return Promise.resolve(Boolean(cached.failed));
	}

	var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
	var timeoutId = controller
		? setTimeout(function () { controller.abort(); }, PROBE_TIMEOUT_MS)
		: null;

	var probeUrl = parsedUrl.protocol + '//' + parsedUrl.host + '/';

	var promise = fetchProbe(probeUrl, {
		mode: 'no-cors',
		cache: 'no-store',
		credentials: 'omit',
		signal: controller ? controller.signal : undefined,
	}).then(function () {
		setProbeCache(domain, { failed: false, checkedAt: Date.now() });
		return false;
	}).catch(function () {
		setProbeCache(domain, { failed: true, checkedAt: Date.now() });
		return true;
	}).finally(function () {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	});

	setProbeCache(domain, { promise: promise });

	return promise;
};

const shouldReportDomainError = (parsedUrl, error, handler) => {
	if (!handler || !isExternalHttps(parsedUrl)) {
		return false;
	}

	if (isOffline() || shouldSkipError(error)) {
		return false;
	}

	if (!fetchProbe) {
		return true;
	}

	return probeDomain(parsedUrl);
};

const reportDomainError = (parsedUrl, source, overrideHandler, error) => {
	var handler = overrideHandler || onDomainError;

	if (!handler) {
		return;
	}

	var decision = shouldReportDomainError(parsedUrl, error, handler);

	if (decision && typeof decision.then === 'function') {
		decision.then(function (shouldReport) {
			if (!shouldReport) {
				return;
			}

			handler({
				domain: parsedUrl.host,
				url: parsedUrl.href,
				source: source,
			});
		});
		return;
	}

	if (!decision) {
		return;
	}

	handler({
		domain: parsedUrl.host,
		url: parsedUrl.href,
		source: source,
	});
};

const isCanceledError = (error) => {
	return Boolean(error && (error.__CANCEL__ || error.code === 'ERR_CANCELED' || error.name === 'AbortError'));
};

const getFetchUrl = (input) => {
	if (typeof input === 'string') {
		return input;
	}

	if (input && typeof input.href === 'string') {
		return input.href;
	}

	if (input && typeof input.url === 'string') {
		return input.url;
	}

	if (input && typeof input.toString === 'function') {
		return input.toString();
	}

	return null;
};

const handleXhrNetworkError = (xhr) => {
	if (!xhr || (xhr.status && xhr.status !== 0)) {
		return;
	}

	var url = resolveUrl(xhr.responseURL || xhr.__certWarningUrl, window.location.origin);
	reportDomainError(url, 'xhr', null, null);
};

export const __testables = {
	resolveUrl,
	isExternalHttps,
	isOffline,
	isHttpError,
	shouldSkipError,
	getFetchUrl,
	isCanceledError,
	getProbeCache,
	setProbeCache,
	probeDomain,
	shouldReportDomainError,
	reportDomainError,
};

export const installCertWarningHandlers = (options) => {
	var opts = options || {};

	if (installed || typeof window === 'undefined') {
		return;
	}

	onDomainError = opts.onDomainError || null;
	installed = true;

	if (window.fetch) {
		fetchProbe = window.fetch.bind(window);
	}

	if (opts.enableFetch !== false && window.fetch) {
		var originalFetch = window.fetch.bind(window);

		window.fetch = function (input, init) {
			var requestUrl = getFetchUrl(input);

			return originalFetch(input, init).catch(function (error) {
				var parsed = resolveUrl(requestUrl, window.location.origin);
				reportDomainError(parsed, 'fetch', null, error);
				throw error;
			});
		};
	}

	if (opts.enableXhr !== false && window.XMLHttpRequest) {
		var originalOpen = window.XMLHttpRequest.prototype.open;
		var originalSend = window.XMLHttpRequest.prototype.send;

		window.XMLHttpRequest.prototype.open = function (method, url) {
			this.__certWarningUrl = url;
			return originalOpen.apply(this, arguments);
		};

		window.XMLHttpRequest.prototype.send = function () {
			var xhr = this;
			var onError = function () {
				handleXhrNetworkError(xhr);
				cleanup();
			};
			var onTimeout = function () {
				handleXhrNetworkError(xhr);
				cleanup();
			};
			var onAbort = function () {
				cleanup();
			};
			var onLoadend = function () {
				cleanup();
			};
			var cleanup = function () {
				xhr.removeEventListener('error', onError);
				xhr.removeEventListener('timeout', onTimeout);
				xhr.removeEventListener('abort', onAbort);
				xhr.removeEventListener('loadend', onLoadend);
			};

			xhr.addEventListener('error', onError);
			xhr.addEventListener('timeout', onTimeout);
			xhr.addEventListener('abort', onAbort);
			xhr.addEventListener('loadend', onLoadend);

			return originalSend.apply(this, arguments);
		};
	}
};

export const attachAxiosCertWarning = (axiosInstance, overrideHandler) => {
	if (!axiosInstance || !axiosInstance.interceptors || axiosInstance.__certWarningAttached) {
		return;
	}

	axiosInstance.__certWarningAttached = true;

	axiosInstance.interceptors.response.use(
		function (response) {
			return response;
		},
		function (error) {
			if (isCanceledError(error) || (error && error.response)) {
				return Promise.reject(error);
			}

			var config = error && error.config ? error.config : {};
			var parsed = resolveUrl(
				config.url,
				config.baseURL || config.baseUrl || window.location.origin,
			);

			reportDomainError(parsed, 'axios', overrideHandler, error);

			return Promise.reject(error);
		},
	);
};
